import { useEffect, useMemo } from 'react';
import { isEmpty } from 'lodash';

import {
  GET_PREBUILT_PRODUCT_GALLERY,
  GET_PRODUCT_GALLERY
} from '../../../../shared/graphql/product-detail-queries';

import { useLazyQuery, useMyAccount } from '../../../../shared/hooks/hooks';

import { getFallbackProductImageUrl } from '../../../../shared/utils/product/product';
import { getGalleryImages } from '../../../../shared/utils/product-detail-utils/product-detail-utils';

import { PRODUCT_IMAGE_FORMATS } from '../../../../shared/constants/product-images';
import { PRODUCT_TYPES } from '../../../../shared/constants/products';

export default function usePdpGalleryImages(
  product,
  productType,
  isPrebuiltPackage,
  isCanonicalSearch,
  selectedSwatch
) {
  const { user } = useMyAccount();

  const {
    data: galleryData,
    error: errorLoadingGallery,
    fetch: getProductGallery,
    hasBeenCalled: isGalleryCalled,
    isLoading: isGalleryLoading
  } = useLazyQuery(GET_PRODUCT_GALLERY);

  const {
    data: prebuiltGalleryData,
    error: errorLoadingPrebuiltGallery,
    fetch: getPrebuiltProductGallery,
    isLoading: isPrebuiltGalleryLoading
  } = useLazyQuery(GET_PREBUILT_PRODUCT_GALLERY);

  const { code: galleryProductCode } = product || {};
  const prebuiltPackage = isPrebuiltPackage ? product : null;

  const galleryImages = useMemo(
    () =>
      getGalleryImages(
        isPrebuiltPackage ? prebuiltGalleryData : galleryData,
        isPrebuiltPackage
      ),
    [galleryData, isPrebuiltPackage, prebuiltGalleryData]
  );

  let fallbackProductImage;

  if (!isGalleryLoading && !isPrebuiltGalleryLoading) {
    if (
      isEmpty(galleryImages) ||
      errorLoadingGallery ||
      errorLoadingPrebuiltGallery
    ) {
      fallbackProductImage = getFallbackProductImageUrl(productType);
    }
  }

  let showFallbackProductImage =
    (!isGalleryLoading && isGalleryCalled && isEmpty(galleryImages)) ||
    Boolean(errorLoadingGallery);

  const productImageFormat = PRODUCT_IMAGE_FORMATS.PRODUCT_MEDIUM;
  const metaImage =
    galleryImages?.[productImageFormat]?.[
      galleryImages?.[productImageFormat]?.length - 1
    ].url ?? fallbackProductImage;

  useEffect(() => {
    if (isCanonicalSearch && productType === PRODUCT_TYPES.WHEELS) {
      if (!selectedSwatch) {
        return;
      }
      getProductGallery({
        color: selectedSwatch?.color,
        productCode: selectedSwatch?.productCode
      });
    } else if (isCanonicalSearch && productType === PRODUCT_TYPES.TIRES) {
      getProductGallery({
        productCode: product?.aggregates?.codes?.[0] ?? galleryProductCode
      });
    } else if (galleryProductCode) {
      getProductGallery({
        productCode: galleryProductCode
      });
    }
  }, [
    productType,
    getProductGallery,
    product,
    isCanonicalSearch,
    selectedSwatch,
    galleryProductCode,
    user?.email
  ]);

  const prebuiltGalleryProductCode1 = prebuiltPackage?.product1?.code;
  const prebuiltGalleryProductCode2 = prebuiltPackage?.product2?.code;

  useEffect(() => {
    if (
      prebuiltGalleryProductCode1 &&
      prebuiltGalleryProductCode2 &&
      isPrebuiltPackage
    ) {
      getPrebuiltProductGallery({
        productCode1: prebuiltGalleryProductCode1,
        productCode2: prebuiltGalleryProductCode2
      });
    }
  }, [
    prebuiltGalleryProductCode1,
    prebuiltGalleryProductCode2,
    getPrebuiltProductGallery,
    isPrebuiltPackage,
    user?.email
  ]);

  return {
    fallbackProductImage,
    galleryImages,
    metaImage,
    showFallbackProductImage
  };
}
